import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import backIcon from "../assets/backIcon.png";
import downloadIcon from "../assets/export-icon.png";
import { gsap } from "gsap";

import ProfileHeader from "../components/Analysis/ProfileHeader";
import BigFiveChart from "../components/Analysis/BigFiveChart";
import DISCModel from "../components/Analysis/DISCModel";
import RIASECModel from "../components/Analysis/RIASECModel";
import MBTISection from "../components/Analysis/MBTISection";
import Recommendations from "../components/Analysis/Recommendations";
import AdvancedAnalysis from "../components/Analysis/AdvancedAnalysis";
import TranslationModal from "../components/TranslationModal";

import SelfAssessment from "../components/Analysis/SelfAssessment";

import ViewModeToggle from "../components/ViewModeToggle";

//Hooks
import usePostAnalysis from "../hooks/usePostAnalysis";
import useProcessFilesBackend from "../hooks/useProcessFilesBackend";
import useGeneratePDF from "../hooks/useGeneratePDF";
import useChartData from "../hooks/useChartData";
import useTranslateAnalysis from "../hooks/useTranslateAnalysis";

import ProgressBar from "../components/Analysis/ProgressBar";
import Error from "../components/Error";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement
);

const translations = {
  false: {
    back: "Back",
    title: "Personality Analysis Report",
    downloadPDF: "Download as PDF",
    generatingPDF: "Generating PDF...",
  },
  true: {
    back: "Terug",
    title: "Persoonlijkheidsanalyse Rapport",
    downloadPDF: "Download als PDF",
    generatingPDF: "PDF genereren...",
  },
};

function Analysis({
  cvFile,
  mLetterfile,
  mLetterOption,
  mLetterText,
  linkedInURL,
  linkedInData,
  isGeneratingPDF,
  setIsGeneratingPDF,
  isRecruiterMode,
  setIsRecruiterMode,
  preferencesSummary,
  reflectionSummary,
  isDutch = false,
  careerPreferences,
  reflection,
}) {
  const navigate = useNavigate();
  const { postAnalysisToAPI } = usePostAnalysis();
  const {
    bigFiveData,
    setBigFiveData,
    discData,
    setDiscData,
    riasecData,
    setRiasecData,
    mbtiData,
    setMbtiData,
  } = useChartData();
  const {
    processFiles,
    analysisResults: processedResults,
    error: processError,
    setError,
    analyzing,
    setAnalyzing,
    progress,
    setProgress,
    analysisResults,
    setAnalysisResults,
    error,
  } = useProcessFilesBackend(
    cvFile,
    mLetterfile,
    mLetterOption,
    mLetterText,
    linkedInData,
    linkedInURL,
    preferencesSummary,
    reflectionSummary,
    setBigFiveData,
    setDiscData,
    setRiasecData,
    setMbtiData,
    careerPreferences,
    reflection
  );
  const {
    generatePDF,
    generatingPDF,
    error: pdfError,
  } = useGeneratePDF(setIsGeneratingPDF);
  const { translatedResults, isTranslating } =
    useTranslateAnalysis(analysisResults);

  const colors = [
    "#FFD700",
    "#FFA500",
    "#FF8C00",
    "#EA650D",
    "#E55100",
    "#D84315",
  ];

  const [isAccountAdmin, setIsAccountAdmin] = useState(false);
  const [currentIsDutch, setCurrentIsDutch] = useState(isDutch);
  const t = translations[currentIsDutch];

  const [questionnaire, setQuestionnaire] = useState({
    id: "",
    user_id: "",
    careerPreferences: {
      desiredRole: "",
      selectedWorkStyles: [],
      selectedMotivators: [],
      workType: "",
      relocation: "",
    },
    reflection: {
      ambition: "",
      ambitionStrategy: "",
      greatestSuccess: "",
      fitnessRituals: "",
    },
    createdAt: "",
    updatedAt: "",
  });

  useEffect(() => {
    const checkLanguage = () => {
      const cookieIsDutch = Cookies.get("isDutch") === "true";
      if (cookieIsDutch !== currentIsDutch) {
        setCurrentIsDutch(cookieIsDutch);
      }
    };

    checkLanguage();

    // Set up an interval to check for changes
    const interval = setInterval(checkLanguage, 1000);

    return () => clearInterval(interval);
  }, [currentIsDutch]);

  useEffect(() => {
    const accountType = Cookies.get("accountType");
    if (!accountType) return;

    if (accountType === "4") {
      setIsRecruiterMode(false);
    } else {
      setIsRecruiterMode(true);
    }

    if (accountType === "1" || accountType === "2") {
      setIsAccountAdmin(true);
    } else {
      setIsAccountAdmin(false);
    }
  }, [setIsRecruiterMode]);

  const apiCallMadeRef = useRef(false);

  useEffect(() => {
    if (analyzing && !apiCallMadeRef.current) {
      apiCallMadeRef.current = true;
      processFiles();
    }
  }, [analyzing, processFiles]);

  useEffect(() => {
    if (!analyzing) {
      apiCallMadeRef.current = false;
    }
  }, [analyzing]);

  const handleGeneratePDF = async () => {
    const formData = new FormData();
    if (cvFile) {
      formData.append("cv_file", cvFile);
    } else {
      formData.append("cv_file", new Blob([], { type: "application/octet-stream" }), "empty.txt");
    }
    formData.append("mLetterfile", mLetterfile);
    formData.append("mLetterOption", mLetterOption);
    formData.append("mLetterText", mLetterText);
    formData.append("linkedInURL", linkedInURL);
    formData.append("preferencesSummary", preferencesSummary);
    formData.append("reflectionSummary", reflectionSummary);
    formData.append("linkedInData", JSON.stringify(linkedInData));

    await generatePDF(
      "analysis-report",
      `${linkedInData.firstName}_${linkedInData.lastName}`,
      formData
    );
  };

  useEffect(() => {
    if (analysisResults && !analyzing && !isTranslating) {
      const sections = document.querySelectorAll(".animate-text");
      const tl = gsap.timeline();

      sections.forEach((section) => {
        tl.to(section, {
          stagger: 0.2,
          duration: 0.2,
          opacity: 1,
          y: 0,
          ease: "back",
        });
      });
    }
  }, [analysisResults, analyzing, isTranslating]);

  useEffect(() => {
    const mainElement = document.querySelector(".main");
    if (mainElement) {
      if (isGeneratingPDF) {
        mainElement.classList.add("mt-neg-active");
      } else {
        mainElement.classList.remove("mt-neg-active");
      }
    }
  }, [isGeneratingPDF]);

  useEffect(() => {
    if (analysisResults) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [analysisResults]);

  useEffect(() => {
    setQuestionnaire((prev) => ({
      ...prev,
      careerPreferences: { ...careerPreferences },
      reflection: { ...reflection },
    }));
  }, [careerPreferences, reflection]);

  return (
    <div className="m-0 text-left pb-10 main">
      {analyzing && (
        <ProgressBar progress={progress} isDutch={currentIsDutch} />
      )}

      {error && <Error error={error} isDutch={currentIsDutch} />}

      {processError && <Error error={processError} isDutch={currentIsDutch} />}

      {pdfError && <Error error={pdfError} isDutch={currentIsDutch} />}

      <TranslationModal isGeneratingPDF={isGeneratingPDF} isOpen={isTranslating} isDutch={currentIsDutch} />

      {!isGeneratingPDF && (
        <div className="flex justify-between items-center">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/upload")}
          >
            <img src={backIcon} alt="back" className="w-5 h-5" />
            <p>{t.back}</p>
          </div>
          {isAccountAdmin && (
            <ViewModeToggle
              isRecruiterMode={isRecruiterMode}
              setIsRecruiterMode={setIsRecruiterMode}
              isDutch={currentIsDutch}
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-4 mt-8 gap-4">
        <h1 className="text-5xl font-bold">{t.title}</h1>
        {!isGeneratingPDF && (
          <button
            className="border px-4 py-2 rounded-md flex items-center gap-2 bg-buttonColor"
            onClick={handleGeneratePDF}
          >
            <img src={downloadIcon} alt="download" className="w-4 h-4" />
            <p className="text-white truncate">
              {generatingPDF ? t.generatingPDF : t.downloadPDF}
            </p>
          </button>
        )}
      </div>

      <main id="analysis-report" className="flex flex-col gap-8">
        {analysisResults && (
          <>
            <>
              <ProfileHeader
                linkedInData={linkedInData}
                analysisResults={translatedResults || analysisResults}
                isRecruiterMode={isRecruiterMode}
                isGeneratingPDF={isGeneratingPDF}
                isDutch={currentIsDutch}
              />
              <AdvancedAnalysis
                analysisResults={translatedResults || analysisResults}
                isRecruiterMode={isRecruiterMode}
                isGeneratingPDF={isGeneratingPDF}
                isDutch={currentIsDutch}
              />
              <section className="flex flex-col gap-8 pb-20">
                <BigFiveChart
                  bigFiveData={bigFiveData}
                  analysisResults={translatedResults || analysisResults}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={currentIsDutch}
                />
                <section className="grid grid-cols-2 gap-4">
                  <DISCModel
                    discData={discData}
                    analysisResults={translatedResults || analysisResults}
                    isRecruiterMode={isRecruiterMode}
                    isDutch={currentIsDutch}
                  />
                  <RIASECModel
                    riasecData={riasecData}
                    analysisResults={translatedResults || analysisResults}
                    colors={colors}
                    isRecruiterMode={isRecruiterMode}
                    isGeneratingPDF={isGeneratingPDF}
                    isDutch={currentIsDutch}
                  />
                </section>
                <MBTISection
                  mbtiData={mbtiData}
                  analysisResults={translatedResults || analysisResults}
                  colors={colors}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={currentIsDutch}
                />
                <Recommendations
                  analysisResults={translatedResults || analysisResults}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={currentIsDutch}
                />

                <SelfAssessment
                  analysisResults={translatedResults || analysisResults}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={currentIsDutch}
                  questionnaire={questionnaire}
                />
              </section>
            </>
          </>
        )}
      </main>
    </div>
  );
}

export default Analysis;
